import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { useMemo, useState } from "react";

import Button from "~/components/buttons/Button/Button.tsx";
import { ComboBoxPlain } from "~/components/formElements/ComboBox.tsx";
import Label from "~/components/formElements/Label/Label.tsx";
import Headline from "~/components/Headline/Headline.tsx";
import Hint from "~/components/Hint";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay.tsx";
import SidebarContent from "~/components/Sidebar/components/SidebarContent.tsx";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter.tsx";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader.tsx";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline.tsx";
import { quotaQueries } from "~/modules/quota/api/quota/quotaQueries.ts";
import type { CreateQuotaConsumerData } from "~/modules/quota/api/quota/quotaTypes.ts";

type Props = {
	companyId: string;
	excludedOrderIds?: string[];
	onCancel: () => void;
	onConfirm: (consumers: CreateQuotaConsumerData[]) => void;
};

const QuotaConsumerSelector = ({ companyId, excludedOrderIds, onCancel, onConfirm }: Props) => {
	const [selectedProjectId, setSelectedProjectId] = useState<string>("");
	const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);

	const {
		data: availableConsumersData,
		isLoading: availableConsumersIsLoading,
	} = useQuery(quotaQueries.availableConsumers(companyId));

	const projectSelectOptions = useMemo(() => {
		return (availableConsumersData?.projects ?? []).map(project => ({
			value: project.id,
			label: project.title,
		}));
	}, [availableConsumersData]);

	// Group the selectable orders under their phase. An excluded order (already
	// assigned to the quota) never enters the map, so it cannot be selected again.
	const ordersByPhaseId = useMemo(() => {
		const map: Record<string, { id: string; title: string }[]> = {};
		(availableConsumersData?.orders ?? []).forEach(order => {
			if (excludedOrderIds?.includes(order.id)) {
				return;
			}
			(map[order.projectPhaseId] ??= []).push({ id: order.id, title: order.title });
		});
		return map;
	}, [availableConsumersData, excludedOrderIds]);

	// Show every project by default. The combobox filters the list down to one project.
	const visibleProjects = useMemo(() => {
		const projects = availableConsumersData?.projects ?? [];
		return selectedProjectId ? projects.filter(project => project.id === selectedProjectId) : projects;
	}, [availableConsumersData, selectedProjectId]);

	const hasSelectableOrders = visibleProjects.some(project =>
		project.phases.some(phase => (ordersByPhaseId[phase.id]?.length ?? 0) > 0),
	);

	const handleOrderClick = (orderId: string) => {
		setSelectedOrderIds(prevState => {
			if (prevState.includes(orderId)) {
				return prevState.filter(id => id !== orderId);
			}
			return [...prevState, orderId];
		});
	};

	const handleConfirm = () => {
		if (selectedOrderIds.length === 0) {
			return;
		}
		const consumers: CreateQuotaConsumerData[] = selectedOrderIds.map(orderId => ({
			orderId,
			topsUpBudget: false,
			budgetCentsAssigned: 0,
			timeTrackingEnabled: true,
		}));
		onConfirm(consumers);
	};

	return <>
		<SidebarHeader>
			<SidebarHeaderHeadline>Bestellungen auswählen</SidebarHeaderHeadline>
		</SidebarHeader>
		<SidebarContent>
			{availableConsumersIsLoading && <SidebarBusyOverlay />}
			<div className="flex items-center justify-center gap-2 border-b border-gray-200 pb-6">
				<div className="flex-1">
					<ComboBoxPlain optionsData={projectSelectOptions}
								   allowNew={false}
								   placeholder="Nach Projekt filtern"
								   onChange={(value) => setSelectedProjectId(typeof value === "string" ? value : "")}
								   value={selectedProjectId} />
				</div>
				<Button theme="primary"
						disabled={!selectedProjectId}
						onClick={() => setSelectedProjectId("")}>
					Zurücksetzen
				</Button>
			</div>
			<div className="mt-4 space-y-4">
				{!availableConsumersIsLoading && hasSelectableOrders && visibleProjects.map(project => {
					const phasesWithOrders = project.phases.filter(
						phase => (ordersByPhaseId[phase.id]?.length ?? 0) > 0,
					);
					if (phasesWithOrders.length === 0) {
						return null;
					}
					return <div key={project.id}
								className="space-y-2">
						<Headline type="h5">{project.title}</Headline>
						{phasesWithOrders.map(phase => {
							return <div key={phase.id}
										className="space-y-1 pl-3">
								<Label>{phase.title}</Label>
								<div className="space-y-2">
									{ordersByPhaseId[phase.id].map(order => {
										const isSelected = selectedOrderIds.includes(order.id);
										return <button key={order.id}
													   type="button"
													   className="group grid w-full grid-cols-[auto_1fr] rounded-lg text-sm hover:bg-gray-100"
													   onClick={() => handleOrderClick(order.id)}>
											<div className={clsx("flex w-10 flex-col items-center justify-center rounded-l-lg border",
												isSelected ? "border-success-600 bg-success-600" : "border-gray-300")}>
												{isSelected ?
													<CheckCircleIcon className="size-6 text-white" /> :
													<div className="size-5 rounded-full border border-gray-300" />}
											</div>
											<div className="rounded-r-lg border border-l-0 border-gray-300">
												<div className="w-full px-4 py-3 text-left">{order.title}</div>
											</div>
										</button>;
									})}
								</div>
							</div>;
						})}
					</div>;
				})}
				{!availableConsumersIsLoading && !hasSelectableOrders &&
					<Hint>
						<p>Für diesen Kunden existieren keine Bestellungen, die einem Abrechnungskontingent zugewiesen werden
							können. Bestellungen können nur dann verwendet werden, wenn die zugehörige Phase nicht geschlossen und die Bestellung bestätigt ist.</p>
					</Hint>}
			</div>
		</SidebarContent>
		<SidebarFooter>
			<Button
				disabled={selectedOrderIds.length === 0}
				onClick={handleConfirm}
			>
				{`${selectedOrderIds.length > 0 ? selectedOrderIds.length + " " : ""}Bestellung${selectedOrderIds.length > 1 ? "en" : ""} auswählen`}
			</Button>
			<Button theme="white"
					onClick={onCancel}>
				Zurück
			</Button>
		</SidebarFooter>
	</>;
};

export default QuotaConsumerSelector;
